import React, { useEffect, useState } from "react";
import { Button, Input, Switch, Space, Typography, Tooltip, message } from "antd";
import { uuid6 } from "../../../helpers/helper";
import { manageWebhooks } from "../../../interfaces/workspaceSettingsInterface";
import { useDispatch, useSelector } from "react-redux";
import CenteredDisplay, { Loader } from "../CenteredContent";
import { setLoader } from "../../../features/rentSlice";

const { Title, Text } = Typography;

const WebhookManager = ({ webhooks = [], onSave , testWebhook }) => {
  const configs = useSelector(state=>state.workspace.webhooks || [])
  const dispatch = useDispatch()
  const [editMode, setEditMode] = useState(null);
  const [activeConfig, setActiveConfig] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    url: "",
    status: true,
    secret: "",
    subscribedEvents: [],
    customHeaders: [{ name: "", value: "" }],
  });

  useEffect(()=>{
    manageWebhooks(dispatch,{},"getAll_webhook")
  },[])
  const availableEvents = [
    {
      type: "rental.added",
      description:
        "A new rental record is created; initiates fulfillment and scheduling.",
    },
    {
      type: "agreement.added",
      description:
        "A rental agreement is established; sets legal and financial basis.",
    },

    {
      type: "invoice.generated",
      description:
        "A billing invoice is created for accounting and payment.",
    },
    {
      type: "agreement.closed",
      description:
        "Rental agreement lifecycle ends; final settlement occurs.",
    },
    {
      type: "inventory.low",
      description:
        "Inventory falls below threshold; triggers replenishment.",
    },
  ];

  const generateSecret = () =>
    `whsec_${Math.random().toString(36).slice(2)}${Math.random()
      .toString(36)
      .slice(2)}`;

  const handleAdd = () => {
    setEditMode("add");
    setActiveConfig(null);
    setFormData({
      name: "",
      url: "",
      status: true,
      secret: generateSecret(),
      subscribedEvents: [],
      customHeaders: [{ name: "", value: "" }],
    });
  };

  const handleEdit = (config) => {
    setEditMode(config.id);
    setActiveConfig(config.id);
    setFormData({
      ...config,
      customHeaders:
        config.customHeaders?.length > 0
          ? config.customHeaders
          : [{ name: "", value: "" }],
    });
  };
  const basicCheck = () => {
     if (!formData.name || !formData.url) {
      message.error("Name and URL are required");
      return false;
    }
    if (!formData.url.startsWith("https://")) {
      message.error("Webhook URL must start with https://");
      return false;
    }
    if (formData.subscribedEvents.length === 0) {
      message.error("Select at least one event");
      return false;
    }
    return true
  }
  const handleTest = () => {
    if(basicCheck()){
        testWebhook(formData)
    }

  }
  const handleSave = () => {
    if(!basicCheck()){
        return
    }
    const cleanedHeaders = formData.customHeaders.filter(
      (h) => h.name && h.value
    );

    if (editMode === "add") {
      const newConfig = {
        ...formData,
        id: uuid6(),
        customHeaders: cleanedHeaders,
      };

      for (var conf of configs){
        if (conf.name === newConfig.name){
            message.error("Webhook Name Already Exist");
            return
        }
      }
      if(configs.length>=25){
        message.error("Webhooks Limit Exceeded , Delete some to add more");
        return
      }


      onSave(newConfig , "add_webhook");
    } else {
      const updated = configs.map((c) =>
        c.id === editMode
          ? { ...formData, id: c.id, customHeaders: cleanedHeaders }
          : c
      );
      if(updated)
      onSave(updated[0] , "update_webhook");
    }

    setEditMode(null);
    setActiveConfig(null);
  };

  const handleDelete = (id) => {
    const updated = configs.filter((c) => c.id !== id);
    if(updated){
        onSave(updated[0] , 'delete_webhook');
    }
    setEditMode(null);
    setActiveConfig(null);
  };

  const copySecret = () => {
    navigator.clipboard.writeText(formData.secret);
    message.success("Secret copied");
  };

  const regenerateSecret = () => {
    setFormData({ ...formData, secret: generateSecret() });
    message.success("Secret regenerated");
  };

  const toggleEvent = (type) => {
    setFormData((prev) => ({
      ...prev,
      subscribedEvents: prev.subscribedEvents.includes(type)
        ? prev.subscribedEvents.filter((e) => e !== type)
        : [...prev.subscribedEvents, type],
    }));
  };

  return (
    <div className="flex h-screen bg-white">
      {/* LEFT */}
      <div
        className={`${
          editMode ? "w-[400px]" : "w-full"
        } border-r border-gray-200 flex flex-col`}
      >
        <div className="p-4 border-b">
          <Title level={4} className="!mb-2">
            Webhooks
          </Title>
          <Button type="primary" block onClick={handleAdd}>
            + Create Webhook
          </Button>
        </div>
        {(!configs || configs.length == 0) && <CenteredDisplay timeout={1000} actionText={""} title={"No Webhooks Found "} description={"Add webhook from the top to get started"} />}
        <div
          className={`${
            editMode
              ? "p-2 overflow-y-auto"
              : "p-4 grid grid-cols-2 gap-3 overflow-y-auto"
          }`}
        >
          {configs.length > 0 && configs.map((c) => (
            <div
              key={c.id}
              onClick={() => handleEdit(c)}
              className={`border rounded-md p-3 mb-2 cursor-pointer transition ${
                activeConfig === c.id
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-200"
              }`}
            >
              <div className="flex justify-between items-center mb-1">
                <div className="font-medium">{c.name}</div>
                <span
                  className={`text-xs px-2 py-[2px] rounded border ${
                    c.status
                      ? "text-green-600 bg-green-50 border-green-200"
                      : "text-red-500 bg-red-50 border-red-200"
                  }`}
                >
                  {c.status ? "Active" : "Inactive"}
                </span>
              </div>

              <div className="text-xs text-gray-500 font-mono truncate mb-1">
                {c.url}
              </div>

              <div className="flex flex-wrap gap-1">
                {c.subscribedEvents.map((e) => (
                  <span
                    key={e}
                    className="text-[11px] px-2 py-[2px] bg-blue-50 border border-blue-200 rounded text-blue-600"
                  >
                    {e}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* RIGHT */}
      {editMode && (
        <div className="flex-1 overflow-y-auto p-6 flex justify-center">
          <div className="w-full max-w-[700px]">
            <Title level={3} className="!mb-1">
              {editMode === "add" ? "Create Webhook" : "Edit Webhook"}
            </Title>
            <Text type="secondary" className="block mb-4">
              Configure webhook endpoint
            </Text>

            <div className="space-y-3">
              {/* NAME + STATUS */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Text strong>Webhook Name</Text>
                  <div className="flex items-center gap-2">
                    <Text className="text-xs">Status</Text>
                    <Switch
                      checked={formData.status}
                      onChange={(v) =>
                        setFormData({ ...formData, status: v })
                      }
                    />
                  </div>
                </div>
                <Input
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                />
              </div>

              {/* URL */}
              <div>
                <Text strong>Webhook URL</Text>
                <Input
                  className="mt-1 font-mono"
                  value={formData.url}
                  onChange={(e) =>
                    setFormData({ ...formData, url: e.target.value })
                  }
                />
              </div>

              {/* SECRET */}
              <div>
                <Text strong>Secret Key</Text>
                <div className="mt-1 flex items-center justify-between border rounded px-3 py-2 bg-gray-50 font-mono text-sm">
                  <span className="break-all">{formData.secret}</span>
                  <Space>
                    <Button size="small" onClick={copySecret}>
                      Copy
                    </Button>
                    <Button size="small" onClick={regenerateSecret}>
                      Regenerate
                    </Button>
                  </Space>
                </div>
              </div>

              {/* HEADERS */}
              <div>
                <Text strong>Custom Headers</Text>
                <div className="mt-1 space-y-1">
                  {formData.customHeaders.map((h, i) => (
                    <div key={i} className="flex gap-2">
                      <Input
                        placeholder="Header"
                        value={h.name}
                        onChange={(e) => {
                          const ch = [...formData.customHeaders];
                          ch[i].name = e.target.value;
                          setFormData({ ...formData, customHeaders: ch });
                        }}
                      />
                      <Input
                        placeholder="Value"
                        value={h.value}
                        onChange={(e) => {
                          const ch = [...formData.customHeaders];
                          ch[i].value = e.target.value;
                          setFormData({ ...formData, customHeaders: ch });
                        }}
                      />
                      {formData.customHeaders.length > 1 && (
                        <Button
                          danger
                          onClick={() =>
                            setFormData({
                              ...formData,
                              customHeaders: formData.customHeaders.filter(
                                (_, idx) => idx !== i
                              ),
                            })
                          }
                        >
                          ×
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <Button
                  type="dashed"
                  block
                  className="mt-1"
                  onClick={() =>
                    setFormData({
                      ...formData,
                      customHeaders: [
                        ...formData.customHeaders,
                        { name: "", value: "" },
                      ],
                    })
                  }
                >
                  + Add Header
                </Button>
              </div>

              {/* EVENTS */}
              <div>
                <Text strong>Events</Text>
                <div className="mt-1 flex flex-wrap gap-2">
                  {availableEvents.map((e) => (
                    <div
                      key={e.type}
                      onClick={() => toggleEvent(e.type)}
                      className={`flex items-center gap-1 px-2 py-1 border rounded cursor-pointer text-xs ${
                        formData.subscribedEvents.includes(e.type)
                          ? "bg-blue-50 border-blue-400 text-blue-600"
                          : "border-gray-300"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.subscribedEvents.includes(e.type)}
                        readOnly
                      />
                      <span>{e.type}</span>
                      <Tooltip title={e.description}>
                        <span className="text-gray-400 cursor-help">ⓘ</span>
                      </Tooltip>
                    </div>
                  ))}
                </div>
              </div>

              {/* ACTIONS */}
              <div className="flex gap-2 pt-2">
                <Button type="primary" onClick={handleSave} className="flex-1">
                  Save
                </Button>
                <Button  onClick={handleTest}>
                  Test
                </Button>
                <Button onClick={() => setEditMode(null)}>Cancel</Button>
                {editMode !== "add" && (
                  <Button danger onClick={() => handleDelete(editMode)}>
                    Delete
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebhookManager;
